import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import { FlightsClassTypes, isRewardFareClass, toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { getSkyLoungeLabel } from 'lib/flights/sharedFlightLabels'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'

export function getCharterFareCabinLabel(intl: IntlShape, classType: FareClassWithReward): string {
  switch (toBaseFareClass(classType)) {
    case FlightsClassTypes.ECONOMY:
      return intl.formatMessage({
        id: 'K7cg5KkP69KFGlsuL7K5',
        defaultMessage: 'Economy',
      })
    case FlightsClassTypes.PREMIUM:
      return intl.formatMessage({
        id: 'mhY8-lGm2cKyrBAYbX5n',
        defaultMessage: 'Economy Plus',
      })
    case FlightsClassTypes.BUSINESS:
      return intl.formatMessage({
        id: 'khVhC_vM-jEVuFUq7pvL',
        defaultMessage: 'Business',
      })
    case FlightsClassTypes.FIRST:
      return intl.formatMessage({
        id: 'DOx8fDJAhNsGpB_-k-h9',
        defaultMessage: 'First',
      })
    case FlightsClassTypes.LUXROW:
    case FlightsClassTypes.LUXROW_PAX_ONE:
      return getSkyLoungeLabel(intl)
  }
}

/**
 * Returns the display label for a charter fare class, including the reward suffix
 * (e.g. "Economy Société Rewards") when the fare class is a reward variant.
 */
export function getCharterFareClassDisplayLabel(intl: IntlShape, fareClass: FareClassWithReward): string {
  const cabinLabel = getCharterFareCabinLabel(intl, fareClass)
  if (!isRewardFareClass(fareClass)) return cabinLabel
  return intl.formatMessage(
    {
      id: 'ncTzfN7UV6N17wsWpmdi',
      defaultMessage: '{cabin} {loyaltyProgramName} Rewards',
    },
    { cabin: cabinLabel, loyaltyProgramName: getLoyaltyProgramDisplayName() },
  )
}

export function getCharterBundleDurationLabel(intl: IntlShape, nights: number): string {
  return intl.formatMessage(
    {
      id: 'UWTJCFgohv6ChMra2PiX',
      defaultMessage: '{nights, plural, one {# night} other {# nights}} & flights',
    },
    { nights },
  )
}

export function getMaldivesBundleLabel(intl: IntlShape): string {
  return intl.formatMessage({
    id: 'f1zpGr0GnPcauF3M9U2F',
    defaultMessage: 'Hotel & Flight bundle',
  })
}

function useCharterFlightLabels() {
  const intl = useIntl()

  return useMemo(() => {
    const charterFareCabinLabelMap: Record<FlightsClassTypes, string> = {
      [FlightsClassTypes.ECONOMY]: getCharterFareCabinLabel(intl, FlightsClassTypes.ECONOMY),
      [FlightsClassTypes.PREMIUM]: getCharterFareCabinLabel(intl, FlightsClassTypes.PREMIUM),
      [FlightsClassTypes.BUSINESS]: getCharterFareCabinLabel(intl, FlightsClassTypes.BUSINESS),
      [FlightsClassTypes.FIRST]: getCharterFareCabinLabel(intl, FlightsClassTypes.FIRST),
      [FlightsClassTypes.LUXROW]: getCharterFareCabinLabel(intl, FlightsClassTypes.LUXROW),
      [FlightsClassTypes.LUXROW_PAX_ONE]: getCharterFareCabinLabel(intl, FlightsClassTypes.LUXROW_PAX_ONE),
    }

    const charterBundleDurationOptions = [
      { value: 5 as const, label: getCharterBundleDurationLabel(intl, 5) },
      { value: 12 as const, label: getCharterBundleDurationLabel(intl, 12) },
    ]

    const fareClassOptions = [
      { value: FlightsClassTypes.ECONOMY, label: charterFareCabinLabelMap[FlightsClassTypes.ECONOMY] },
      { value: FlightsClassTypes.PREMIUM, label: charterFareCabinLabelMap[FlightsClassTypes.PREMIUM] },
      { value: FlightsClassTypes.LUXROW, label: charterFareCabinLabelMap[FlightsClassTypes.LUXROW] },
      { value: FlightsClassTypes.BUSINESS, label: charterFareCabinLabelMap[FlightsClassTypes.BUSINESS] },
    ]

    const maldivesBundleLabel = getMaldivesBundleLabel(intl)

    return {
      charterFareCabinLabelMap,
      charterBundleDurationOptions,
      fareClassOptions,
      maldivesBundleLabel,
    }
  }, [intl])
}

export default useCharterFlightLabels
